// client/src/SignInModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import useBackdropClose from './useBackdropClose';
import * as API from './api';
import { shouldAutoCloseAuthModal } from './authFlow';
import { validatePublicUsername } from './authUsername';
import { createBotChecker } from './botCheck';
import Honeypot from './Honeypot';

/* ------------------------------- auth calls ------------------------------- */

// Sign-in always goes through the server; there is no local fallback that
// would keep passwords in the browser.
const api = (API && (API.default ?? API)) || {};
const safeAPI = {
  register: (payload) => api.register(payload),
  login: (payload) => api.login(payload),
};

/* ---------------------------- sign-up agreements ---------------------------- */

// Bump when the wording changes materially; the server records which version was accepted.
export const TERMS_VERSION = '2026-10-13';

export const SIGNUP_AGREEMENTS = [
  {
    key: 'age',
    text: 'I am at least 16 years old.',
  },
  {
    key: 'privacy',
    text:
      'Frolik keeps as little about me as possible: no real name, email address, phone number, or location. ' +
      'Only my username, my password in scrambled form, a profile picture if I add one, and the events and comments I post are stored, ' +
      'plus, if I tap "Notify me" on an event, a notification address for this device. ' +
      'Saved likes stay on this device.',
  },
  {
    key: 'conduct',
    text:
      'I will not use Frolik for anything illegal, including scams, selling illegal goods or services, ' +
      'harassment, or planning harm. Events or accounts that break this can be removed, and Frolik ' +
      'cooperates with valid requests from authorities. I accept the Terms of Service and have read the Privacy Policy.',
  },
  {
    key: 'location',
    text:
      'If I use "Use my location", or leave an event\'s location empty, only an approximate position ' +
      '(rounded to about 100\u00a0m) is sent to Frolik\'s map provider to find my city. Frolik never stores ' +
      'my position, and my city is only used as an event\'s location if I confirm it.',
  },
];

/* ------------------------------- modal ------------------------------- */

export default function SignInModal({
  open,
  initialMode = 'login',
  onClose,
  onSignedIn,
}) {
  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <ModalContent
      key={initialMode}
      initialMode={initialMode}
      onClose={onClose}
      onSignedIn={onSignedIn}
    />,
    document.body
  );
}

function ModalContent({
  initialMode = 'login',
  onClose,
  onSignedIn,
}) {
  const [mode, setMode] =
    useState(initialMode);

  // Bot check for sign-up: start solving as soon as the box opens.
  const botCheckerRef = useRef(null);
  const honeypotRef = useRef(null);
  useEffect(() => {
    botCheckerRef.current = createBotChecker();
    botCheckerRef.current.start();
  }, []);

  const [password, setPassword] =
    useState('');

  const [username, setUsername] =
    useState('');

  const [busy, setBusy] =
    useState(false);

  const backdropHandlers = useBackdropClose(onClose);

  const [error, setError] =
    useState('');

  const [successMessage, setSuccessMessage] = useState('');
  const [agreements, setAgreements] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const allAgreed = SIGNUP_AGREEMENTS.every((item) => agreements[item.key]);
  const showAgreementStep = mode === 'register' && !agreedToTerms;

  const switchMode = () => {
    setMode((currentMode) =>
      currentMode === 'login'
        ? 'register'
        : 'login'
    );

    setError('');
    setSuccessMessage('');
  };

  const submit = async (event) => {
    event.preventDefault();

    if (busy) {
      return;
    }

    setBusy(true);
    setError('');
    setSuccessMessage('');

    if (mode === 'register' && !agreedToTerms) {
      setError('Please accept the sign-up agreements first.');
      setBusy(false);
      return;
    }

    try {
      let signedInUser;

      if (mode === 'login') {
        signedInUser =
          await safeAPI.login({
            username: username.trim(),
            password,
          });
      } else {
        const usernameError = validatePublicUsername(username);

        if (usernameError) {
          setError(usernameError);
          setBusy(false);
          return;
        }

        // Bot check (solved in the background since the box opened), plus the
        // hidden "website" field people never see. One retry with a fresh
        // check if the old one expired.
        const registerWith = async () => safeAPI.register({
          password,
          username: username.trim(),
          acceptedTerms: true,
          termsVersion: TERMS_VERSION,
          website: honeypotRef.current ? honeypotRef.current.value : '',
          botCheck: await botCheckerRef.current.take(),
        });
        signedInUser = await registerWith();
        if (signedInUser && signedInUser.botCheck) signedInUser = await registerWith();
      }

      if (
        signedInUser &&
        !signedInUser.error
      ) {
        localStorage.setItem(
          'auth_user',
          JSON.stringify(
            signedInUser
          )
        );

        localStorage.setItem(
          'auth_token',
          signedInUser.token ||
            'cookie'
        );

        const shouldClose = shouldAutoCloseAuthModal(mode);

        onSignedIn?.(
          signedInUser,
          { mode, keepOpen: !shouldClose }
        );

        if (shouldClose) {
          onClose?.();
          return;
        }

        setSuccessMessage(
          mode === 'register'
            ? 'Account created. You are signed in and can keep browsing.'
            : 'Signed in successfully.'
        );

        if (mode === 'register') {
          setMode('login');
          setPassword('');
          setUsername('');
        }

        return;
      }

      setError(
        signedInUser?.error ||
          'Unknown error. No response was received.'
      );
    } catch (requestError) {
      console.error(
        'Auth error:',
        requestError
      );

      setError(
        requestError?.message ||
          'Request failed.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={backdrop}
      {...backdropHandlers}
      aria-modal="true"
      role="dialog"
      aria-labelledby="auth-modal-title"
    >
      <div
        style={card}
        onClick={(event) =>
          event.stopPropagation()
        }
      >
        <h3
          id="auth-modal-title"
          style={{
            margin: 0,
            marginBottom: 12,
            fontWeight: 700,
          }}
        >
          {mode === 'login'
            ? 'Sign in'
            : showAgreementStep
              ? 'Before you sign up'
              : 'Create an account'}
        </h3>

        {showAgreementStep && (
          <div>
            <p style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.5, color: '#374151' }}>
              Please read and agree to the following to continue.
            </p>

            <div style={{ marginBottom: 14, display: 'grid', gap: 10 }}>
              {SIGNUP_AGREEMENTS.map((item) => (
                <label
                  key={item.key}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, lineHeight: 1.5 }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(agreements[item.key])}
                    onChange={(event) => {
                      // Read now: React resets controlled checkboxes before the updater runs.
                      const { checked } = event.target;
                      setAgreements((current) => ({ ...current, [item.key]: checked }));
                    }}
                    style={{ marginTop: 2 }}
                  />
                  <span>{item.text}</span>
                </label>
              ))}
            </div>

            <p style={{ margin: '0 0 14px', fontSize: 12 }}>
              Read the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>

            <div style={buttonRow}>
              <button
                type="button"
                onClick={() => setAgreedToTerms(true)}
                disabled={!allAgreed}
                style={{
                  ...primaryBtn,
                  ...(!allAgreed ? disabledButton : {}),
                }}
              >
                Continue
              </button>

              <button type="button" onClick={switchMode} style={ghostBtn}>
                Already have an account?
              </button>

              <button type="button" onClick={onClose} style={ghostBtn}>
                Close
              </button>
            </div>
          </div>
        )}

        {!showAgreementStep && (

        <form
          onSubmit={submit}
          style={{
            width: '100%',
            position: 'relative',
          }}
        >
          <Honeypot ref={honeypotRef} />
          <div
            style={{
              marginBottom: 12,
            }}
          >
            <label
              htmlFor="auth-username"
              style={label}
            >
              Username
            </label>

            <input
              id="auth-username"
              type="text"
              value={username}
              autoComplete="username"
              onChange={(event) =>
                setUsername(
                  event.target.value
                )
              }
              style={input}
              placeholder={mode === 'login' ? 'Your public username' : 'Choose a public username'}
              required
              minLength={3}
              maxLength={24}
              disabled={busy}
            />

            <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5, color: '#4b5563' }}>
              Choose a public username only. Never use your real name, legal name, or anything identifying you.
            </div>
          </div>

          <div
            style={{
              marginBottom: 14,
            }}
          >
            <label
              htmlFor="auth-password"
              style={label}
            >
              Password
            </label>

            <input
              id="auth-password"
              type="password"
              value={password}
              autoComplete={
                mode === 'login'
                  ? 'current-password'
                  : 'new-password'
              }
              onChange={(event) =>
                setPassword(
                  event.target.value
                )
              }
              style={input}
              placeholder={
                mode === 'login'
                  ? 'Your password'
                  : 'Create a password'
              }
              required
              minLength={6}
              disabled={busy}
            />
          </div>

          {mode === 'login' && (
            <div style={{ marginBottom: 12, fontSize: 12, lineHeight: 1.5, color: '#374151' }}>
              Saved likes stay on this device only. Location sharing, if enabled, is coarse and approximate.
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                color: 'crimson',
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {successMessage && (
            <div
              role="status"
              style={{
                color: '#166534',
                marginBottom: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {successMessage}
            </div>
          )}

          <div style={buttonRow}>
            <button
              type="submit"
              disabled={busy}
              style={{
                ...primaryBtn,
                ...(busy
                  ? disabledButton
                  : {}),
              }}
            >
              {busy
                ? 'Please wait…'
                : mode === 'login'
                  ? 'Sign in'
                  : 'Create account'}
            </button>

            <button
              type="button"
              onClick={switchMode}
              disabled={busy}
              style={{
                ...ghostBtn,
                ...(busy
                  ? disabledButton
                  : {}),
              }}
            >
              {mode === 'login'
                ? 'Need an account?'
                : 'Already have an account?'}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{
                ...ghostBtn,
                ...(busy
                  ? disabledButton
                  : {}),
              }}
            >
              Close
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

/* -------------------------------- styles -------------------------------- */

const backdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  backdropFilter: 'blur(2px)',
  WebkitBackdropFilter:
    'blur(2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: 16,
};

const card = {
  width: 'min(480px, 92vw)',
  background: '#fff',
  borderRadius: 6,

  boxShadow:
    '0 12px 30px rgba(0,0,0,0.15)',

  padding: 20,
  boxSizing: 'border-box',
};

const label = {
  display: 'block',
  fontSize: 12,
  marginBottom: 6,
};

const input = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 5,
  border: '1px solid #ccc',
  outline: 'none',
  fontSize: 14,
};

const buttonRow = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};

const primaryBtn = {
  padding: '10px 14px',
  borderRadius: '10px',
  background: '#111',
  color: '#fff',
  border: '1px solid #111',
  cursor: 'pointer',
};

const ghostBtn = {
  padding: '10px 14px',
  borderRadius: '10px',
  background: 'transparent',
  color: '#111',
  border: '1px solid #ddd',
  cursor: 'pointer',
};

const disabledButton = {
  opacity: 0.6,
  cursor: 'not-allowed',
};